import { useMemo, useRef, useState, Fragment } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import {
  Search,
  Sliders,
  X,
  Tag,
  Package,
  MessageCircle,
  Instagram,
  Send,
  Star,
  MapPin,
} from 'react-feather';
import { CATEGORIES, ORDER_LINKS } from '../utils/constants';
import { PRODUCTS } from '../utils/products';
import heroImg from 'assets/hero.jpg';

function formatUGX(n) {
  try {
    return `UGX ${n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  } catch {
    return `UGX ${n}`;
  }
}

export function Shop() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('featured'); // 'price-asc' | 'price-desc' | 'name'
  const [selected, setSelected] = useState(null); // product
  const [filterOpen, setFilterOpen] = useState(false);

  // Hero parallax
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.8]);

  const filtered = useMemo(() => {
    let list = PRODUCTS.filter((p) => (category === 'all' ? true : p.category === category)).filter(
      (p) =>
        `${p.name} ${p.tags.join(' ')} ${p.description}`.toLowerCase().includes(query.toLowerCase())
    );

    if (sort === 'featured') {
      list = [...list].sort((a, b) => (b.featured === true) - (a.featured === true));
    } else if (sort === 'price-asc') {
      list = [...list].sort((a, b) => a.priceUGX - b.priceUGX);
    } else if (sort === 'price-desc') {
      list = [...list].sort((a, b) => b.priceUGX - a.priceUGX);
    } else if (sort === 'name') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [category, query, sort]);

  const waMessage = (product) =>
    encodeURIComponent(
      `Hello Gambit Games! I'm interested in "${product.name}". Price: ${formatUGX(
        product.priceUGX
      )}. Could you help me place an order for delivery within Kampala?`
    );

  return (
    <WebLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative isolate overflow-hidden bg-gray-950">
        <motion.div
          style={{ y, opacity }}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
        >
          {/* drop your cover image under /public/assets/hero_shop.jpg */}
          <img src={heroImg} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/60 to-gray-950" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#12A2BF]" />
              Made in Uganda • Free delivery within Kampala.
            </div>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Handcrafted Wooden Board Games
            </h1>
            <p className="mt-3 max-w-xl text-sm text-gray-300">
              Classics, cultural favorites, travel companions, party starters, and fully custom
              builds crafted in the Pearl of Africa.
            </p>
          </div>

          {/* Quick filters */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`rounded-full px-3 py-1.5 text-sm ${
                  category === c.id
                    ? 'bg-[#12A2BF] text-black'
                    : 'bg-gray-900 text-gray-200 hover:bg-gray-800'
                }`}
              >
                {c.label}
              </button>
            ))}
            <button
              onClick={() => setFilterOpen(true)}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-gray-900 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-800"
            >
              <Sliders size={16} />
              Filters
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 flex items-center overflow-hidden rounded-xl border border-gray-800 bg-gray-900">
            <div className="px-3 text-gray-400">
              <Search size={18} />
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Chess, Ludo, Omweso, Travel…"
              className="w-full bg-transparent px-2 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none"
            />
            <div className="border-l border-gray-800">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-full bg-gray-900 px-3 py-3 text-sm text-gray-200 focus:outline-none"
                aria-label="Sort products"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low → High</option>
                <option value="price-desc">Price: High → Low</option>
                <option value="name">Name A–Z</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="relative bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 lg:px-8">
          {/* Grid content */}
          {filtered.length === 0 ? (
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-8 text-center">
              <p className="text-gray-300">
                No items match your search. Try another keyword or category.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p, i) => (
                <motion.button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className="group overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 text-left"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.35 }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-800">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {/* badges */}
                    <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-2">
                      {p.featured && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-900">
                          <Star size={14} />
                          Featured
                        </span>
                      )}
                      {p.category === 'custom' && (
                        <span className="rounded-full bg-purple-400/90 px-2 py-0.5 text-xs font-semibold text-purple-900">
                          Custom
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 p-4">
                    <h3 className="line-clamp-1 text-base font-semibold text-white">{p.name}</h3>
                    <div className="flex items-center justify-between">
                      <p className="text-[#12A2BF]">{formatUGX(p.priceUGX)}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <MapPin size={14} />
                        Kampala delivery
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {p.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-800 bg-gray-950 px-2 py-0.5 text-[11px] text-gray-300"
                        >
                          <Tag size={12} />
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FILTERS SHEET (mobile/simple) */}
      <Transition show={filterOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[70]" onClose={setFilterOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0">
            <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-2xl">
              <Transition.Child
                as={Fragment}
                enter="transition transform duration-300 ease-out"
                enterFrom="translate-y-full"
                enterTo="translate-y-0"
                leave="transition transform duration-200 ease-in"
                leaveFrom="translate-y-0"
                leaveTo="translate-y-full"
              >
                <Dialog.Panel className="rounded-t-2xl border border-gray-800 bg-gray-900 p-6 shadow-2xl">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title className="text-sm font-semibold text-white">
                      Filters
                    </Dialog.Title>
                    <button
                      onClick={() => setFilterOpen(false)}
                      className="rounded-md p-1 text-gray-300 hover:text-white"
                      aria-label="Close filters"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs text-gray-400">Category</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => setCategory(c.id)}
                            className={`rounded-full px-3 py-1.5 text-sm ${
                              category === c.id
                                ? 'bg-[#12A2BF] text-black'
                                : 'bg-gray-800 text-gray-200'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400">Sort by</label>
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="mt-2 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200"
                      >
                        <option value="featured">Featured</option>
                        <option value="price-asc">Price: Low → High</option>
                        <option value="price-desc">Price: High → Low</option>
                        <option value="name">Name A–Z</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      className="rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
                      onClick={() => {
                        setCategory('all');
                        setSort('featured');
                        setQuery('');
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-black hover:brightness-110"
                      onClick={() => setFilterOpen(false)}
                    >
                      Apply
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* PRODUCT DIALOG */}
      <Transition show={!!selected} as={Fragment}>
        <Dialog as="div" className="relative z-[80]" onClose={() => setSelected(null)}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="transition transform duration-300 ease-out"
                enterFrom="translate-y-6 opacity-0"
                enterTo="translate-y-0 opacity-100"
                leave="transition transform duration-200 ease-in"
                leaveFrom="translate-y-0 opacity-100"
                leaveTo="translate-y-6 opacity-0"
              >
                <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 shadow-2xl">
                  {selected && (
                    <div className="grid gap-0 sm:grid-cols-2">
                      <div className="relative aspect-square bg-gray-800">
                        <img
                          src={selected.image}
                          alt={selected.name}
                          className="h-full w-full object-cover"
                        />
                        {selected.featured && (
                          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-900">
                            <Star size={14} />
                            Featured
                          </span>
                        )}
                      </div>

                      <div className="p-5">
                        <div className="flex items-start justify-between gap-2">
                          <Dialog.Title className="text-lg font-semibold text-white">
                            {selected.name}
                          </Dialog.Title>
                          <button
                            onClick={() => setSelected(null)}
                            className="rounded-md p-1 text-gray-300 hover:text-white"
                            aria-label="Close product details"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        <p className="mt-2 text-sm text-gray-300">{selected.description}</p>

                        <dl className="mt-4 space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-400">Price</dt>
                            <dd className="font-semibold text-[#12A2BF]">
                              {formatUGX(selected.priceUGX)}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-400">Material</dt>
                            <dd className="text-gray-200">{selected.material}</dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-400">Size</dt>
                            <dd className="text-gray-200">{selected.size}</dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-400">Lead time</dt>
                            <dd className="text-gray-200">
                              {selected.leadTimeDays} day{selected.leadTimeDays > 1 ? 's' : ''}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-400">Delivery</dt>
                            <dd className="text-gray-200">Within Kampala</dd>
                          </div>
                        </dl>

                        {/* Order CTAs */}
                        <div className="mt-5 grid gap-2 sm:grid-cols-3">
                          <a
                            href={`https://wa.me/${ORDER_LINKS.whatsappNumber}?text=${waMessage(
                              selected
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#12A2BF] px-3 py-2 text-sm font-semibold text-black hover:brightness-110"
                          >
                            <MessageCircle size={16} />
                            WhatsApp
                          </a>
                          <a
                            href={`${ORDER_LINKS.instagram}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-pink-500/90 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                          >
                            <Instagram size={16} />
                            Instagram
                          </a>
                          <a
                            href={`${ORDER_LINKS.telegram}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                          >
                            <Send size={16} />
                            Telegram
                          </a>
                        </div>

                        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                          <Package size={14} />
                          Handcrafted with love • Free delivery within Kampala • Pay for product on
                          delivery.
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </WebLayout>
  );
}
